import PropTypes from 'prop-types';
import React from 'react';
import Option from './Option';

const Options = ({
  arrowDirection,
  filterString,
  focusedIndex,
  applySelection,
  handleCategorySelect,
  htmlId,
  multiselect,
  optionComponent,
  options,
  path,
  selection,
  setArrowDirection,
  setFocusedIndex,
  setIsActionsFocused,
  setIsFilterFocused,
  setParentName,
  setPath,
  setSelection,
  showGroups,
  ...rest
}) => {
  const getSelectionIds = () => selection.map(object => object.id);
  const isInSelection = (option) => getSelectionIds().indexOf(option.id) > -1;

  const handleCheckboxChange = (option, checked) => {
    const newOption = { path, ...option };
    if (checked && !isInSelection(newOption)) {
      setSelection(prev => prev.concat(newOption));
    } else {
      setSelection(prev => prev.filter(curr => curr.id !== option.id));
    }
  };

  const hasChildren = (obj) => {
    if (obj.child && obj.child.length === 0) return false;
    if (obj.child) return true;
    return false;
  };

  const isFocusable = (option) => !((showGroups && hasChildren(option)) || !!option.disabled);

  const getNextFocusableItem = (currentIndex) => {
    let newFocusedIndex = currentIndex;
    const optionIndex = newFocusedIndex - 1; /* currentIndex is one based! */
    for (let newIdx = optionIndex + 1; newIdx < options.length; newIdx += 1) {
      if (isFocusable(options[newIdx])) {
        newFocusedIndex = newIdx + 1; /* currentIndex is one based! */
        break;
      }
    }
    return newFocusedIndex;
  };

  const getPrevFocusableItem = (currentIndex) => {
    let newFocusedIndex = currentIndex || 1;
    const optionIndex = newFocusedIndex - 1; /* currentIndex is one based! */
    for (let newIdx = optionIndex - 1; newIdx >= 0; newIdx -= 1) {
      if (isFocusable(options[newIdx])) {
        newFocusedIndex = newIdx + 1;
        break;
      }
    }
    return newFocusedIndex;
  };

  const handleOptionClick = (option, isCategory = false) => {
    if (hasChildren(option) && isCategory) {
      handleCategorySelect(option);
    } else {
      const newOption = { path, ...option };
      applySelection([newOption]);
    }
  };

  const handleKeyDown = (event, option, isCheckbox) => {
    setIsActionsFocused(false);
    event.preventDefault();

    if (event.key === 'ArrowUp') {
      setArrowDirection('up');
      setFocusedIndex(prev => {
        let newPrevItem = getPrevFocusableItem(prev);
        if (newPrevItem === prev) {
          setIsFilterFocused(true);
          newPrevItem = undefined;
        }
        return newPrevItem;
      });
    }

    if (event.key === 'ArrowDown') {
      setArrowDirection('down');
      setFocusedIndex(prev => getNextFocusableItem(prev));
    }

    if (event.key === 'ArrowRight') {
      if (hasChildren(option)) {
        handleCategorySelect(option);
        setArrowDirection('down');
        setFocusedIndex(() => getNextFocusableItem(0));
      }
    }

    if (event.key === 'ArrowLeft') {
      setArrowDirection('down');
      if (path.length > 1) {
        setParentName(path[path.length - 2].id);
        setPath(path.slice(0, path.length - 1));
        setFocusedIndex(prev => getPrevFocusableItem(prev));
      } else if (path.length) {
        setParentName(undefined);
        setPath([]);
      }
    }

    if (event.key === 'Enter' || event.keyCode === 32) { // Space
      setArrowDirection('down');
      if (isCheckbox) {
        const { target: { checked } } = event;
        handleCheckboxChange(option, !checked);
      } else {
        handleOptionClick(option);
      }
    }

    if (event.key === 'Tab') {
      setArrowDirection('down');
      setIsActionsFocused(true);
    }
  };

  const setFocus = (option, focused, optionRef) => {
    if ((option.disabled || (hasChildren(option) && showGroups)) && focused) {
      setFocusedIndex(prev => getNextFocusableItem(prev));
    } else if (focused) {
      if (multiselect) {
        optionRef.current.querySelector('input').focus();
      } else {
        optionRef.current.focus();
      }
    }
  };

  if (optionComponent) {
    const CustomOption = optionComponent;
    return options.map((option, idx) => (
      <CustomOption
        focused={focusedIndex === idx + 1}
        handleKeyDown={handleKeyDown}
        htmlId={htmlId}
        key={option.id}
        setFocus={setFocus}
        isFocused={focusedIndex === idx + 1}
        onClick={handleOptionClick}
        option={option}
      />
    ));
  }

  return options.map((option, idx) => (
    <Option
      filterString={filterString}
      handleKeyDown={handleKeyDown}
      htmlId={htmlId}
      isCategory={hasChildren(option)}
      isFocused={focusedIndex === idx + 1}
      isOwnGroup={!option.parentName && showGroups}
      isSelected={isInSelection(option)}
      key={option.id}
      multiselect={multiselect}
      onChange={(name, checked) => { handleCheckboxChange(option, checked); }}
      onClick={handleOptionClick}
      option={option}
      setFocus={setFocus}
      showGroups={showGroups}
      {...rest}
    />
  ));
};

Options.propTypes = {
  applySelection: PropTypes.func.isRequired,
  handleCategorySelect: PropTypes.func.isRequired,
  htmlId: PropTypes.string.isRequired,
  setArrowDirection: PropTypes.func.isRequired,
  setFocusedIndex: PropTypes.func.isRequired,
  setIsActionsFocused: PropTypes.func.isRequired,
  setParentName: PropTypes.func.isRequired,
  setPath: PropTypes.func.isRequired,
  setSelection: PropTypes.func.isRequired,

  /** The direction to look for enabled options if focus failed on a disabled option */
  arrowDirection: PropTypes.string.isRequired,

  /** The user's typed input which options are filtered by  */
  filterString: PropTypes.string.isRequired,

  /** Index of the option which should receive focus. Expects undefined or int */
  focusedIndex: PropTypes.number,

  /** UX setting to allow multiple selections */
  multiselect: PropTypes.bool.isRequired,

  /** React component for rendering an individual option */
  optionComponent: PropTypes.func,

  /** All options for the current tree level */
  options: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    disabled: PropTypes.bool,
    highlight: PropTypes.bool
  })).isRequired,

  /** List of options representing the breadcrumb path */
  path: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired
  })).isRequired,

  /** All options currently selected */
  selection: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired
  })).isRequired,

  /** Display setting for representing parent options as flat headers */
  showGroups: PropTypes.bool.isRequired
};

Options.defaultProps = {
  path: undefined,
  focusedIndex: undefined,
  optionComponent: undefined
};

export default Options;
